import React from 'react';
import { Mic, MicOff, Volume2, X, HelpCircle } from 'lucide-react';
import { useVoiceCommands } from '../../context/VoiceCommandContext';

const VoiceIndicator = () => {
  const {
    isListening,
    isEnabled,
    interimTranscript,
    transcript,
    feedbackMessage,
    listeningMode,
    recognitionSupported,
    stopListening,
    toggleEnabled,
  } = useVoiceCommands();

  // Don't render if not supported
  if (!recognitionSupported) return null;

  // Determine what to show
  const showIndicator = isListening || feedbackMessage;

  return (
    <>
      {/* Main floating indicator - shows when listening or has feedback */}
      {showIndicator && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] animate-slide-down">
          <div className={`
            flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl border backdrop-blur-sm
            ${isListening
              ? 'bg-gradient-to-r from-slate-900/95 to-blue-900/95 border-cyan-500/50'
              : feedbackMessage?.type === 'success'
              ? 'bg-green-900/95 border-green-500/50'
              : feedbackMessage?.type === 'error'
              ? 'bg-red-900/95 border-red-500/50'
              : feedbackMessage?.type === 'warning'
              ? 'bg-yellow-900/95 border-yellow-500/50'
              : 'bg-slate-900/95 border-slate-500/50'
            }
          `}>
            {/* Icon */}
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center
              ${isListening ? 'bg-cyan-500 animate-pulse' : 'bg-slate-700'}
            `}>
              {isListening ? (
                <Mic className="w-5 h-5 text-white" />
              ) : feedbackMessage?.type === 'success' ? (
                <Volume2 className="w-5 h-5 text-green-300" />
              ) : feedbackMessage?.type === 'error' ? (
                <MicOff className="w-5 h-5 text-red-300" />
              ) : (
                <Volume2 className="w-5 h-5 text-slate-300" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {isListening ? (
                <>
                  <p className="text-sm font-medium text-white">
                    {listeningMode === 'continuous' ? 'Listening (continuous)...' : 'Listening...'}
                  </p>
                  <p className="text-xs text-cyan-300 truncate max-w-[200px]">
                    {interimTranscript || transcript || 'Say a command...'}
                  </p>
                </>
              ) : feedbackMessage ? (
                <p className={`text-sm font-medium ${
                  feedbackMessage.type === 'success' ? 'text-green-200' :
                  feedbackMessage.type === 'error' ? 'text-red-200' :
                  feedbackMessage.type === 'warning' ? 'text-yellow-200' :
                  'text-slate-200'
                }`}>
                  {feedbackMessage.message}
                </p>
              ) : null}
            </div>

            {/* Waveform animation when listening */}
            {isListening && (
              <div className="flex items-center gap-0.5 h-6">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-cyan-400 rounded-full animate-wave"
                    style={{
                      animationDelay: `${i * 0.1}s`,
                      height: '100%',
                    }}
                  />
                ))}
              </div>
            )}

            {/* Close button when listening */}
            {isListening && (
              <button
                onClick={stopListening}
                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-cyan-300" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Small status indicator in corner - shows voice status */}
      <div className="fixed bottom-24 right-6 z-40">
        <button
          onClick={toggleEnabled}
          className={`
            p-2 rounded-full shadow-lg transition-all
            ${isEnabled
              ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
              : 'bg-slate-700 hover:bg-slate-600 text-slate-400'
            }
            ${listeningMode === 'wake-word' && isEnabled ? 'ring-2 ring-cyan-400 ring-opacity-50' : ''}
          `}
          title={isEnabled ? 'Voice commands active (click to disable)' : 'Voice commands disabled (click to enable)'}
        >
          {isEnabled ? (
            <Mic className="w-4 h-4" />
          ) : (
            <MicOff className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Styles */}
      <style>{`
        @keyframes slide-down {
          from {
            transform: translateX(-50%) translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-down {
          animation: slide-down 0.2s ease-out forwards;
        }

        @keyframes wave {
          0%, 100% {
            transform: scaleY(0.3);
          }
          50% {
            transform: scaleY(1);
          }
        }
        .animate-wave {
          animation: wave 0.6s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default VoiceIndicator;
